import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { appMessage as message } from '../utils/appMessage';
import { JobStatus } from '../types/job';
import { useCardPage } from './CardPageContext';

interface ProductRegistrationJobContextValue {
  activeJobId: string | null;
  setActiveJobId: (id: string | null) => void;
  detailJob: JobStatus | null;
  setDetailJob: (job: JobStatus | null) => void;
  handleComplete: (job: JobStatus) => void;
}

const ProductRegistrationJobContext = createContext<ProductRegistrationJobContextValue | null>(null);

/**
 * "상품 등록"(REGISTER_PRODUCTS) 잡 상태를 페이지 바깥(MainApp 최상위)에서 관리한다
 * (2026-08-20) — 예전엔 ProductRegistrationPage 안에서만 activeJobId를 로컬 state로
 * 관리해서(그 페이지 주석에 "카드 관리의 CardJobBar와 공유하지 않고 로컬로 띄운다"고
 * 명시돼 있었음), 등록이 진행되는 도중 "반영 로그" 등 다른 페이지로 이동했다가 돌아오면
 * 페이지가 언마운트→재마운트되며 activeJobId가 사라져 상단 진행바가 통째로 없어졌다 —
 * 백엔드 잡(JobProgressService)은 그대로 돌고 있는데 프론트만 놓치는 것뿐이었다(사용자가
 * 실사용 중 발견, 정확히 이 원인을 스스로 짚었음). CardJobBar/StockJobBar(MainApp.tsx)와
 * 같은 이유로 Provider를 최상위로 옮긴다 — jobId/폴링만 옮기고, 확장팩 선택·검색 결과 같은
 * 페이지 고유 상태는 그대로 ProductRegistrationPage에 남긴다.
 */
export function ProductRegistrationJobProvider({ children }: { children: ReactNode }) {
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [detailJob, setDetailJob]     = useState<JobStatus | null>(null);
  const { cards } = useCardPage();

  const handleComplete = useCallback((job: JobStatus) => {
    // 끝난 잡(성공 · 실패 · 서버에서 사라짐)은 잠금을 푼다 — 예전엔 비우는 곳이 없어 등록을 한 번 하면 SPA 안에서
    // 등록 버튼이 "다른 상품 등록 작업이 진행 중입니다" 로 영구히 잠겼다(감사 card-controltower#84). Stock · Card 잡의
    // handleJobComplete 가 setActiveJobId(null) 하는 것과 같다.
    setActiveJobId(null);
    setDetailJob(job);
    if (job.status === 'DONE') {
      if (job.dryRunCount > 0) {
        // naver.commerce.register-enabled=false라 실제로는 Naver에 아무것도 안
        // 올라간 상태 — 초록 성공 토스트를 쓰면 진짜 등록과 구분이 안 된다
        // (2026-08-19, NAVER_REGISTER_ENABLED 환경변수 누락으로 겪은 실제 사고).
        message.warning(
          `⚠️ DRY-RUN — 실제로는 등록 안 됨 (${job.dryRunCount}건). 서버 설정(NAVER_REGISTER_ENABLED)을 확인해주세요.`,
          6,
        );
      } else {
        message.success(
          `상품 등록 완료 — 성공 ${job.successCount}건${job.failCount > 0 ? ` / 실패 ${job.failCount}건` : ''}`,
        );
        // "스토어 상품 관리"(CardPageContext)의 카드 목록은 이 화면과 별개로 앱 최상위에서
        // 한 번만 불러와 캐시해두는 구조라, 여기서 새로 등록해도 저쪽은 그 사실을 모른다 —
        // 등록 직후 바로 "스토어 상품 관리"로 이동해도 새로고침 전까진 옛 목록이 그대로
        // 보였다(2026-08-25 사용자 리포트: 네이버엔 46건 올라갔는데 화면 전체 건수는
        // 그대로였음 — 브라우저 새로고침하면 바로 보였던 걸 보면 서버 반영 자체는 이미
        // 돼 있었고 프론트 캐시만 안 갱신된 것). IMPORT_PRODUCTS 완료 때
        // reloadCards()하는 것과 같은 패턴으로, 실제 등록 성공(dry-run 아님)이 1건이라도
        // 있으면 카드 목록도 같이 새로 불러온다.
        if (job.successCount > 0) {
          cards.reload();
        }
      }
    } else {
      message.error(`상품 등록 실패 — ${job.error ?? '알 수 없는 오류'}`);
    }
  }, [cards]);

  const value = useMemo(() => ({
    activeJobId, setActiveJobId, detailJob, setDetailJob, handleComplete,
  }), [activeJobId, detailJob, handleComplete]);

  return (
    <ProductRegistrationJobContext.Provider value={value}>
      {children}
    </ProductRegistrationJobContext.Provider>
  );
}

export function useProductRegistrationJob(): ProductRegistrationJobContextValue {
  const ctx = useContext(ProductRegistrationJobContext);
  if (!ctx) throw new Error('useProductRegistrationJob must be used within ProductRegistrationJobProvider');
  return ctx;
}
